/**
 * TabNavigationBar Component
 *
 * Vertical icon rail for navigating the IRP Workbook's analytical views.
 * Each tab is an icon with a hover tooltip label; the active tab is highlighted
 * and the Flagged Reports tab carries a review-count badge. Connected to the
 * Zustand store for tab state.
 */

import React from 'react';
import {Badge, Box, IconButton, Tooltip} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BubbleChartOutlinedIcon from '@mui/icons-material/BubbleChartOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import {TabKey, useIRPWorkbookStore} from '../../../../../../stores/irp-workbook.store';
import {useIRPContext} from '../IRPWorkbookProvider';

interface RailTab {
    key: TabKey;
    label: string;
    icon: React.ReactElement;
}

const RAIL_TABS: RailTab[] = [
    { key: 'overview', label: 'Overview', icon: <DashboardOutlinedIcon /> },
    { key: 'skipped-reports', label: 'Flagged Reports', icon: <FlagOutlinedIcon /> },
    { key: 'precursor-barrier', label: 'Precursor/Control Review', icon: <ShieldOutlinedIcon /> },
    { key: 'report-analysis', label: 'Report Level Analysis', icon: <ArticleOutlinedIcon /> },
    { key: 'barrier-effectiveness', label: 'Barrier Effectiveness', icon: <AssessmentOutlinedIcon /> },
    { key: 'occurrence-patterns', label: 'Report Clustering', icon: <BubbleChartOutlinedIcon /> },
    { key: 'ntsb-recommendations', label: 'NTSB Recommendations', icon: <GavelOutlinedIcon /> },
];

/**
 * Vertical icon tab rail for the IRP Workbook.
 */
const TabNavigationBar: React.FC = () => {
    const activeTab = useIRPWorkbookStore((state) => state.activeTab);
    const setActiveTab = useIRPWorkbookStore((state) => state.setActiveTab);
    const skippedReportCount = useIRPWorkbookStore(
        (state) => state.overviewCache?.skippedReportCount ?? 0
    );
    const integrityIssueCount = useIRPWorkbookStore(
        (state) => state.overviewCache?.integrityIssueCount ?? 0
    );
    const reviewCount = skippedReportCount + integrityIssueCount;

    // Component evidence has no skipped-report review flow, so the Flagged Reports tab is hidden.
    const {componentMapping} = useIRPContext();
    const railTabs = componentMapping
        ? RAIL_TABS.filter((tab) => tab.key !== 'skipped-reports')
        : RAIL_TABS;

    return (
        <Box
            role="tablist"
            aria-label="IRP workbook view tabs"
            aria-orientation="vertical"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                py: 0.5,
            }}
        >
            {railTabs.map((tab) => {
                const selected = activeTab === tab.key;
                const badge = tab.key === 'skipped-reports' ? reviewCount : 0;
                return (
                    <Tooltip key={tab.key} title={tab.label} placement="right">
                        <IconButton
                            role="tab"
                            aria-label={tab.label}
                            aria-selected={selected}
                            onClick={(): void => setActiveTab(tab.key)}
                            sx={{
                                width: 52,
                                height: 48,
                                borderRadius: 0,
                                color: selected ? 'primary.main' : 'text.secondary',
                                borderLeft: '3px solid',
                                borderLeftColor: selected ? 'primary.main' : 'transparent',
                                backgroundColor: selected ? 'action.selected' : 'transparent',
                                '&:hover': {
                                    backgroundColor: selected ? 'action.selected' : 'action.hover',
                                },
                            }}
                        >
                            <Badge
                                badgeContent={badge}
                                color="warning"
                                overlap="circular"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}
                            >
                                {tab.icon}
                            </Badge>
                        </IconButton>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

export default TabNavigationBar;
